import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';
import { parseError } from '@state/errors.parser';
import * as uscActions from './user-supporting-company.actions';
import { UserSupportingCompanyHttpService } from '@core/http/user-supporting-company.http.service';

@Injectable()
export class UserSupportingCompanyEffects {
  private readonly actions$ = inject(Actions);
  private readonly uscHttp = inject(UserSupportingCompanyHttpService);
  private readonly snackService = inject(StackedMessagesService);

  getCompaniesSupportedByUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uscActions.getCompaniesSupportedByUser),
      switchMap(({ userId }) => {
        return this.uscHttp.getCompaniesSupportedByUser(userId).pipe(
          map(companiesSupportedByUser => uscActions.getCompaniesSupportedByUserSuccess({ companiesSupportedByUser })),
          catchError(error => {
            const errorMessage = parseError(error, uscActions.getCompaniesSupportedByUser.type);
            this.snackService.showError({
              message: 'MANAGEMENT.EMPLOYEE_DETAILS.ERROR_FETCHING_LIST_OF_SUPPORTING_COMPANIES',
            });
            return of(uscActions.getCompaniesSupportedByUserError({ errorMessage }));
          })
        );
      })
    )
  );

  getUsersSupportingCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uscActions.getUsersSupportingCompany),
      switchMap(({ companyId }) => {
        return this.uscHttp.getUsersSupportingCompany(companyId).pipe(
          map(usersSupportingCompany => uscActions.getUsersSupportingCompanySuccess({ usersSupportingCompany })),
          catchError(error => {
            const errorMessage = parseError(error, uscActions.getUsersSupportingCompany.type);
            this.snackService.showError({
              message: 'MANAGEMENT.COMPANY_DETAILS.ERROR_FETCHING_LIST_OF_SUPPORTING_EMPLOYEES',
            });
            return of(uscActions.getUsersSupportingCompanyError({ errorMessage }));
          })
        );
      })
    )
  );

  updateSupportingCompanyOfUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(uscActions.updateSupportingCompanyOfUser),
      switchMap(({ opts }) => {
        return this.uscHttp.updateSupportingCompanyOfUser(opts).pipe(
          map(userSupportingCompany => uscActions.updateSupportingCompanyOfUserSuccess({ userSupportingCompany })),
          catchError(error => {
            const errorMessage = parseError(error, uscActions.updateSupportingCompanyOfUser.type);
            this.snackService.showError({ message: 'MANAGEMENT.ERROR_UPDATING_SUPPORTED_COMPANY' });
            return of(uscActions.updateSupportingCompanyOfUserError({ errorMessage }));
          })
        );
      })
    )
  );
}
